import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { endOfDay, startOfDay } from 'date-fns'
import moment from 'moment'

import { i18nDateToString } from 'modules/core/lib/dates'

export interface PageDateFilters {
  startDate: Date
  endDate: Date
}

interface PageDateFiltersProviderProps {
  children: React.ReactNode
}

interface PageDateFiltersContext {
  dateFilters: PageDateFilters
  setPageDateFilters: (startDate: Date, endDate: Date) => void
}

const PageDateFiltersContext = React.createContext<PageDateFiltersContext | undefined>(undefined)

export const PageDateFiltersProvider: React.FC<
  React.PropsWithChildren<PageDateFiltersProviderProps>
> = ({ children }) => {
  const defaultFilters = (searchParams?: URLSearchParams): PageDateFilters => {
    if (!searchParams?.get('startDate') && !searchParams?.get('endDate')) {
      return {
        startDate: startOfDay(moment().subtract(30, 'days').toDate()),
        endDate: endOfDay(new Date()),
      }
    }
    return {
      startDate: startOfDay(moment(searchParams.get('startDate'), true).toDate()),
      endDate: endOfDay(moment(searchParams.get('endDate'), true).toDate()),
    }
  }

  const [params, setParams] = useSearchParams()

  const [dateFilters, setDateFilters] = React.useState<PageDateFilters>(defaultFilters(params))

  const setPageDateFilters = (startDate: Date, endDate: Date) => {
    setDateFilters({
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
    })
  }

  useUnsafeEffect(() => {
    setParams(
      {
        startDate: i18nDateToString(dateFilters.startDate, 'backend'),
        endDate: i18nDateToString(dateFilters.endDate, 'backend'),
      },
      { replace: true }
    )
  }, [dateFilters, setParams])

  return (
    <PageDateFiltersContext.Provider
      value={{
        dateFilters,
        setPageDateFilters,
      }}
    >
      {children}
    </PageDateFiltersContext.Provider>
  )
}

export const usePageDateFilters = () => {
  const context = React.useContext(PageDateFiltersContext)

  if (context === undefined) {
    throw new Error('usePageDateFilters must be used within a PageDateFiltersProvider')
  }

  return context
}
